import md5 from "blueimp-md5";
import round from "lodash/round";
import React, { FunctionComponent, useMemo } from "react";
import { FilterTag, filterTagToSVGComponent } from "../../../../agreed/filters";
import { Cropping } from "../../../../types/customization/content";
import "./style.scss";

interface Props {
  cropping: Cropping;
  filterTag: FilterTag;
  heightPx: number;
  imageUrl: string;
  semiOpaque?: boolean;
  shapeSvgContent?: string;
  widthPx: number;
}

const EditablePictureSvg: FunctionComponent<Props> = ({
  cropping,
  filterTag,
  imageUrl,
  heightPx,
  semiOpaque,
  shapeSvgContent,
  widthPx,
}) => {
  const maskId = useMemo(() => {
    if (!shapeSvgContent) {
      return "";
    }

    return `${md5(shapeSvgContent)}-${round(widthPx, 1)}-${round(heightPx, 1)}`;
  }, [shapeSvgContent, widthPx, heightPx]);

  let maskSVGElement: React.ReactElement<React.SVGProps<SVGMaskElement>> | undefined;
  if (shapeSvgContent) {
    maskSVGElement = (
      <mask
        id={`EditablePictureSvg-mask-${maskId}`}
        maskContentUnits="userSpaceOnUse"
        maskUnits="userSpaceOnUse"
      >
        {semiOpaque ? (
          <rect fill="#666" height="100%" strokeWidth="0" width="100%" x="0" y="0" />
        ) : null}
        <g
          dangerouslySetInnerHTML={{ __html: shapeSvgContent }}
          height="100%"
          width="100%"
          x="0"
          y="0"
        />
      </mask>
    );
  }

  const imageWidthPx = round((widthPx * 10000) / cropping.width.value);
  const imageHeightPx = round((heightPx * 10000) / cropping.height.value);

  const leftPx = -1 * round((imageWidthPx * cropping.x.value) / 10000);
  const topPx = -1 * round((imageHeightPx * cropping.y.value) / 10000);

  const rotationLeft = leftPx + imageWidthPx / 2;
  const rotationTop = topPx + imageHeightPx / 2;

  const filterSVGElementId = `${filterTag}-${round(widthPx, 1)}-${round(heightPx, 1)}`;

  const isRotated = cropping.rotation % 180 === 90;
  const filterSVGElement = useMemo(() => {
    const FilterSVGComponent = filterTagToSVGComponent(filterTag);
    if (!FilterSVGComponent) {
      return null;
    }

    return (
      <FilterSVGComponent
        height={isRotated ? imageWidthPx : imageHeightPx}
        id={filterSVGElementId}
        key={filterTag}
        width={isRotated ? imageHeightPx : imageWidthPx}
      />
    );
  }, [filterTag, imageHeightPx, imageWidthPx, isRotated, leftPx, topPx]);

  const isSafari = window.navigator.userAgent.includes("Safari");

  return (
    <svg className="EditablePictureSvg" viewBox={`0 0 ${widthPx} ${heightPx}`}>
      <defs>
        {filterSVGElement}
        {maskSVGElement}
      </defs>

      <g
        filter={
          filterSVGElement ? `url(#${filterSVGElementId})` : undefined
        } /* for Safari and the filter change bug */
        key={filterTag}
        mask={maskSVGElement?.props?.id ? `url(#${maskSVGElement.props.id})` : undefined}
      >
        <image
          height={imageHeightPx}
          href={isSafari ? undefined : imageUrl}
          preserveAspectRatio="none"
          transform={`rotate(${cropping.rotation * -1} ${rotationLeft} ${rotationTop})`}
          width={imageWidthPx}
          x={leftPx}
          xlinkHref={isSafari ? imageUrl : undefined}
          y={topPx}
        />
      </g>
    </svg>
  );
};

export default EditablePictureSvg;
